import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";

import { ApiError } from "@/api/client";
import { t } from "@/i18n";
import { SoftphoneDevice } from "./device";
import { dtmfKey } from "./dtmf";
import { fetchVoiceToken, useHangup, useStartCall } from "./queries";
import { BRIDGE, SOFTPHONE } from "./state";
import { awaitsSummary, canSendTones, isCallLive, useSoftphoneStore } from "./store";

/**
 * Who to call. Not *how*: every call is placed from the browser.
 *
 * The bridge to the agent's own mobile used to be a choice in *Ajustes*, and
 * the owner closed it (D51) — one way to call, with the mute, the keypad and
 * the recording all behaving the same for everybody. The server refuses a
 * bridged call outright, so this is not only the SPA being polite.
 */
export type StartCallRequest = {
  /** A `ContactPhone` pk. What links the call to a company and its timeline. */
  phone?: number;
  /** A hand-typed prospecting number. */
  number?: string;
  /** Typed on the *Llamar* keypad: validated, and from the agent's own number. */
  keypad?: boolean;
  /** Who is being called, for the bar. Cosmetic. */
  label?: string;
};

type Softphone = {
  start: (request: StartCallRequest) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  /** Press keys on the answered call — an extension, a menu option. */
  sendDigits: (digits: string) => void;
  /** Grant the microphone and build the `Device`. Must run inside a click. */
  prepare: () => Promise<void>;
  /** Talk into this microphone on calls; `null` is the system default. */
  chooseInput: (deviceId: string | null) => void;
  /** The microphone calls will use. Read once, not subscribed to. */
  chosenInput: () => string | null;
  isStarting: boolean;
};

const SoftphoneContext = createContext<Softphone | null>(null);

export function useSoftphone(): Softphone {
  const softphone = useContext(SoftphoneContext);
  if (!softphone) {
    throw new Error("useSoftphone debe usarse dentro de <SoftphoneProvider>.");
  }
  return softphone;
}

/**
 * Owns the Twilio `Device` and the lifetime of a call.
 *
 * Mounted once above the router (`App`), so a call survives navigation. Selling
 * means clicking around the CRM while talking, and a softphone that drops when
 * the agent opens the client's history is a softphone nobody uses.
 */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const startCall = useStartCall();
  const hangupCall = useHangup();

  const begin = useSoftphoneStore((state) => state.begin);
  const applyDevice = useSoftphoneStore((state) => state.applyDevice);
  const setMuted = useSoftphoneStore((state) => state.setMuted);
  const noteDigits = useSoftphoneStore((state) => state.noteDigits);
  const setDeviceReady = useSoftphoneStore((state) => state.setDeviceReady);
  const setError = useSoftphoneStore((state) => state.setError);
  const clear = useSoftphoneStore((state) => state.clear);

  const device = useMemo(
    () =>
      new SoftphoneDevice({
        fetchToken: async () => (await fetchVoiceToken()).token,
        onError: setError,
        onCallState: (state) => {
          applyDevice(state);
          // The browser leg ended. The server is authoritative for *why* and for
          // the duration, and its callback may not have landed yet, so ask
          // rather than inventing a final state from the SDK's silence.
          if (state === "idle") {
            const { call } = useSoftphoneStore.getState();
            if (call && call.mode === SOFTPHONE) {
              void queryClient.invalidateQueries({
                queryKey: ["call", call.ref],
              });
            }
          }
        },
      }),
    [applyDevice, queryClient, setError],
  );

  useEffect(() => () => device.destroy(), [device]);

  const prepare = useCallback(async () => {
    await device.ensureReady();
    setDeviceReady(device.isReady);
  }, [device, setDeviceReady]);

  const start = useCallback(
    async (request: StartCallRequest) => {
      setError(null);

      // Before the POST, not after. Both the microphone prompt and the
      // `AudioContext` need the click that got us here to still be the
      // browser's most recent user gesture; a round trip to Django first is
      // long enough to lose it.
      try {
        await prepare();
      } catch {
        return; // `ensureReady` has already said what went wrong.
      }

      try {
        const call = await startCall.mutateAsync({
          mode: SOFTPHONE,
          ...(request.phone !== undefined ? { phone: request.phone } : {}),
          ...(request.number !== undefined ? { number: request.number } : {}),
          ...(request.keypad ? { keypad: true } : {}),
        });
        begin({
          ref: call.ref,
          mode: SOFTPHONE,
          dialedNumber: call.dialed_number,
          label: request.label ?? "",
          status: call.status,
          keypad: request.keypad ?? false,
        });
        await device.connect(call.ref);
        // Hung up while the browser leg was still being set up. There was no
        // leg to end then, so the bar was simply cleared — and this one would
        // now go on to ring the client with nothing on screen to stop it.
        if (useSoftphoneStore.getState().call?.ref !== call.ref) device.disconnect();
      } catch (error) {
        // `start_call` refuses with a reason worth reading: no caller ID
        // registered, no mobile on the profile, Twilio unconfigured.
        if (error instanceof ApiError) {
          setError(error.message);
        } else if (!useSoftphoneStore.getState().error) {
          // Only when nothing better has been said. A rejected `Device.connect`
          // is usually preceded by the SDK's own `error` event, which names the
          // Twilio code — overwriting it with this leaves the agent, and
          // whoever they call for help, with nothing to go on.
          setError(t("errors.startCall"));
        }
        // The row exists but no browser leg ever came up, so there is nothing
        // for `Colgar` to hang up. Leaving the bar on screen strands the agent
        // with a button that cannot work and a call that will not end.
        clear();
      }
    },
    [begin, clear, device, prepare, setError, startCall],
  );

  const hangup = useCallback(() => {
    const { call } = useSoftphoneStore.getState();
    if (!call) return;
    if (call.mode === BRIDGE) {
      // No browser leg to end. Bridge mode is two carrier legs, and only
      // Twilio can drop them.
      hangupCall.mutate(call.ref);
    } else if (device.hasCall) {
      device.disconnect();
    } else {
      // Softphone, but the SDK never got a leg — the token was refused, the
      // signalling socket never opened. `disconnect()` would be a no-op and the
      // bar would sit there for the rest of the session.
      clear();
    }
  }, [clear, device, hangupCall]);

  const toggleMute = useCallback(() => {
    const { call } = useSoftphoneStore.getState();
    if (!call || call.mode === BRIDGE) return;
    device.mute(!call.muted);
    setMuted(!call.muted);
  }, [device, setMuted]);

  const sendDigits = useCallback(
    (digits: string) => {
      if (!canSendTones(useSoftphoneStore.getState().call) || !device.hasCall) return;
      device.sendDigits(digits);
      noteDigits(digits);
    },
    [device, noteDigits],
  );

  // The computer's own keys, on whatever screen the agent is on. A switchboard
  // asks for an extension while they are looking at the company, the agenda or
  // the keypad, and reaching for the mouse to open a pad is how the menu times
  // out. Heard here, once, rather than in the call bar: the agenda's modal
  // mounts a second bar, and two listeners would send every key twice.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // A held key is one press, not twenty tones.
      if (event.defaultPrevented || event.repeat) return;
      const key = dtmfKey(event);
      if (key === null || !canSendTones(useSoftphoneStore.getState().call)) return;
      const target = event.target instanceof Element ? event.target : null;
      // Typing a note or a search during the call is typing, not dialling.
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      sendDigits(key);
      event.preventDefault();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sendDigits]);

  // A seller who closes the tab mid-call drops it, and on a softphone leg
  // there is nothing on the other side to notice. One who closes it, or
  // reloads, after an answered call loses the summary they still owe — the
  // only way the wrap-up lets that call go. The browser only honours this if
  // the page has been interacted with, which during a call it has.
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      const { call } = useSoftphoneStore.getState();
      if (isCallLive(call) || awaitsSummary(call)) event.preventDefault();
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, []);

  const chooseInput = useCallback(
    (deviceId: string | null) => device.chooseInput(deviceId),
    [device],
  );
  const chosenInput = useCallback(() => device.inputDevice, [device]);

  const value = useMemo<Softphone>(
    () => ({
      start,
      hangup,
      toggleMute,
      sendDigits,
      prepare,
      chooseInput,
      chosenInput,
      isStarting: startCall.isPending,
    }),
    [
      start,
      hangup,
      toggleMute,
      sendDigits,
      prepare,
      chooseInput,
      chosenInput,
      startCall.isPending,
    ],
  );

  return (
    <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>
  );
}
